import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../utils/api';

/**
 * TemplatesPage - Browse and use category templates
 * Includes system templates and community-created templates
 */
const TemplatesPage = ({ token, showToast }) => {
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [communityTemplates, setCommunityTemplates] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('system');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Form state for creating templates
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    protocol: '',
    category: 'General',
    tags: [],
    icon: '📁',
    is_public: false
  });

  // Simple fetch function using XMLHttpRequest to bypass service worker
  const simpleFetch = useCallback((url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      
      // Set headers
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              resolve(xhr.responseText);
            }
          } else {
            reject({ status: xhr.status, message: xhr.responseText });
          }
        }
      };
      
      xhr.onerror = () => reject({ status: 0, message: 'Network error' });
      xhr.send(options.body || null);
    });
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    
    try {
      // Fetch system templates
      const systemData = await simpleFetch(`${API}/templates/system`);
      setSystemTemplates(systemData.templates || []);
    } catch (e) {
      console.error('Error fetching system templates:', e);
    }

    try {
      // Fetch community templates
      const communityData = await simpleFetch(`${API}/templates/community?limit=50`);
      setCommunityTemplates(communityData.templates || []);
    } catch (e) {
      console.error('Error fetching community templates:', e);
    }

    // Fetch my templates (if logged in)
    if (token) {
      try {
        const myData = await simpleFetch(`${API}/templates/my-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyTemplates(myData.templates || []);
      } catch (e) {
        console.error('Error fetching my templates:', e);
      }
    }
    
    setLoading(false);
  }, [token, simpleFetch]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await simpleFetch(`${API}/templates/categories`);
      setCategories(data.categories || []);
    } catch (e) {
      console.error('Error fetching categories:', e);
    }
  }, [simpleFetch]);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [fetchTemplates, fetchCategories]);

  const useTemplate = async (templateId) => {
    if (!token) {
      showToast && showToast('Please log in to use templates', 'error');
      return;
    }

    try {
      const data = await simpleFetch(`${API}/templates/use/${templateId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      showToast && showToast(`✅ ${data.message}`, 'success');
    } catch (e) {
      showToast && showToast(e.message || 'Failed to use template', 'error');
    }
  };

  const createTemplate = async () => {
    if (!token) {
      showToast && showToast('Please log in to create templates', 'error');
      return;
    }

    if (!newTemplate.name.trim() || !newTemplate.protocol.trim()) {
      showToast && showToast('Name and protocol are required', 'error');
      return;
    }

    try {
      const data = await simpleFetch(`${API}/templates/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newTemplate)
      });
      
      showToast && showToast(`✅ ${data.message}`, 'success');
      setShowCreateModal(false);
      setNewTemplate({
        name: '',
        description: '',
        protocol: '',
        category: 'General',
        tags: [],
        icon: '📁',
        is_public: false
      });
      fetchTemplates();
    } catch (e) {
      showToast && showToast(e.message || 'Failed to create template', 'error');
    }
  };

  const filteredTemplates = (templates) => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(t => 
      t.name.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.tags?.some(tag => tag.toLowerCase().includes(q))
    );
  };

  const getTemplatesByTab = () => {
    switch (activeTab) {
      case 'system':
        return filteredTemplates(systemTemplates);
      case 'community':
        return filteredTemplates(communityTemplates);
      case 'my':
        return filteredTemplates(myTemplates);
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FDFBF7, #FFFFFF)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: '#047857', fontSize: '1.5rem' }}>Loading templates...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FDFBF7, #FFFFFF)',
      padding: '20px'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 30,
          flexWrap: 'wrap',
          gap: 15
        }}>
          <div>
            <h1 style={{ 
              color: '#047857', 
              fontSize: '2rem', 
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              📋 Category Templates
            </h1>
            <p style={{ color: '#71717a', fontSize: '0.95rem' }}>
              Pre-configured protocols to get you started quickly
            </p>
          </div>
          
          {token && (
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
                border: 'none',
                color: '#0F172A',
                padding: '12px 24px',
                borderRadius: 12,
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              data-testid="create-template-btn"
            >
              + Create Template
            </button>
          )}
        </div>

        {/* Search and Tabs */}
        <div style={{ marginBottom: 25 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            style={{
              width: '100%',
              padding: '14px 20px',
              borderRadius: 12,
              border: '1px solid rgba(253, 224, 71, 0.3)',
              background: 'rgba(0,0,0,0.3)',
              color: '#334155',
              fontSize: '1rem',
              marginBottom: 15,
              boxSizing: 'border-box'
            }}
            data-testid="template-search"
          />
          
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {['system', 'community', 'my'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 10,
                  border: 'none',
                  background: activeTab === tab 
                    ? 'linear-gradient(135deg, #FDE047, #FCD34D)' 
                    : 'rgba(255, 255, 255, 0.85)',
                  color: activeTab === tab ? '#fff' : '#a1a1aa',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                data-testid={`tab-${tab}`}
              >
                {tab === 'system' && 'System'}
                {tab === 'community' && 'Community'}
                {tab === 'my' && 'My Templates'}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 20
        }}>
          {getTemplatesByTab().map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onUse={() => useTemplate(template.id)}
            />
          ))}
        </div>

        {getTemplatesByTab().length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: 60,
            color: '#71717a'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 15 }}>📋</div>
            <div style={{ fontSize: '1.2rem' }}>No templates found</div>
            {activeTab === 'my' && token && (
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  marginTop: 20,
                  background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
                  border: 'none',
                  color: '#0F172A',
                  padding: '12px 24px',
                  borderRadius: 12,
                  cursor: 'pointer'
                }}
              >
                Create Your First Template
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <CreateTemplateModal
          newTemplate={newTemplate}
          setNewTemplate={setNewTemplate}
          onClose={() => setShowCreateModal(false)}
          onCreate={createTemplate}
        />
      )}
    </div>
  );
};

/**
 * TemplateCard - Individual template display card
 */
const TemplateCard = ({ template, onUse }) => {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(253, 251, 247, 0.98))',
        borderRadius: 16,
        padding: 20,
        border: '1px solid rgba(253, 224, 71, 0.2)',
        transition: 'border-color 0.2s'
      }}
      data-testid={`template-card-${template.id}`}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 15, marginBottom: 15 }}>
        <div style={{
          fontSize: '2rem',
          width: 50,
          height: 50,
          background: 'rgba(253, 224, 71, 0.2)',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {template.icon || '📁'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ color: '#334155', fontSize: '1.1rem', marginBottom: 4 }}>
            {template.name}
          </h3>
          <span style={{
            background: 'rgba(253, 224, 71, 0.2)',
            color: '#047857',
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: '0.7rem'
          }}>
            {template.category || 'General'}
          </span>
        </div>
      </div>

      <p style={{
        color: '#94a3b8',
        fontSize: '0.9rem',
        marginBottom: 15,
        minHeight: 40
      }}>
        {template.description || 'No description provided'}
      </p>

      <div style={{
        background: 'rgba(0,0,0,0.3)',
        padding: '10px 12px',
        borderRadius: 8,
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: '#047857',
        marginBottom: 15,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {template.protocol}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {template.usage_count > 0 && (
            <span style={{ color: '#71717a', fontSize: '0.8rem' }}>
              {template.usage_count} uses
            </span>
          )}
          {template.rating > 0 && (
            <span style={{ color: '#047857', fontSize: '0.8rem' }}>
              {template.rating}
            </span>
          )}
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUse();
          }}
          style={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            border: 'none',
            color: '#0F172A',
            padding: '8px 16px',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
          data-testid={`use-template-${template.id}`}
        >
          Use
        </button>
      </div>
    </div>
  );
};

/**
 * CreateTemplateModal - Modal for creating new templates
 */
const CreateTemplateModal = ({ newTemplate, setNewTemplate, onClose, onCreate }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1025, #0f0a15)',
          borderRadius: 20,
          padding: 30,
          width: '90%',
          maxWidth: 550,
          border: '1px solid rgba(253, 224, 71, 0.3)',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ color: '#047857', marginBottom: 20 }}>
          Create New Template
        </h2>

        <div style={{ marginBottom: 15 }}>
          <label style={{ color: '#334155', display: 'block', marginBottom: 6, fontSize: '0.9rem' }}>
            Template Name *
          </label>
          <input
            type="text"
            value={newTemplate.name}
            onChange={e => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., News Aggregation"
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: '1px solid rgba(253, 224, 71, 0.3)',
              background: 'rgba(0,0,0,0.3)',
              color: '#334155',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 15 }}>
          <label style={{ color: '#334155', display: 'block', marginBottom: 6, fontSize: '0.9rem' }}>
            Description
          </label>
          <textarea
            value={newTemplate.description}
            onChange={e => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
            placeholder="What does this template help with?"
            rows={2}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: '1px solid rgba(253, 224, 71, 0.3)',
              background: 'rgba(0,0,0,0.3)',
              color: '#334155',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 15 }}>
          <label style={{ color: '#334155', display: 'block', marginBottom: 6, fontSize: '0.9rem' }}>
            Protocol *
          </label>
          <textarea
            value={newTemplate.protocol}
            onChange={e => setNewTemplate(prev => ({ ...prev, protocol: e.target.value }))}
            placeholder="(keyword1 OR keyword2) AND requirement -exclusion"
            rows={3}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 10,
              border: '1px solid rgba(253, 224, 71, 0.3)',
              background: 'rgba(0,0,0,0.3)',
              color: '#334155',
              fontFamily: 'monospace',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 15, marginBottom: 15 }}>
          <div style={{ flex: 1 }}>
            <label style={{ color: '#334155', display: 'block', marginBottom: 6, fontSize: '0.9rem' }}>
              Category
            </label>
            <select
              value={newTemplate.category}
              onChange={e => setNewTemplate(prev => ({ ...prev, category: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 10,
                border: '1px solid rgba(253, 224, 71, 0.3)',
                background: 'rgba(0,0,0,0.3)',
                color: '#334155'
              }}
            >
              {['General', 'Technology', 'Academic', 'Media', 'Health', 'Career', 'Shopping', 'Social', 'Development', 'Learning'].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ color: '#334155', display: 'block', marginBottom: 6, fontSize: '0.9rem' }}>
              Icon
            </label>
            <select
              value={newTemplate.icon}
              onChange={e => setNewTemplate(prev => ({ ...prev, icon: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 10,
                border: '1px solid rgba(253, 224, 71, 0.3)',
                background: 'rgba(0,0,0,0.3)',
                color: '#334155'
              }}
            >
              {['📁', '📰', '📚', '💻', '📱', '🎓', '⭐', '🏥', '📖', '💼', '🔓', '📊', '🔬', '🎯'].map(icon => (
                <option key={icon} value={icon}>{icon}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            color: '#334155'
          }}>
            <input
              type="checkbox"
              checked={newTemplate.is_public}
              onChange={e => setNewTemplate(prev => ({ ...prev, is_public: e.target.checked }))}
              style={{ accentColor: '#FDE047', width: 18, height: 18 }}
            />
            Share with community
          </label>
          <p style={{ color: '#71717a', fontSize: '0.8rem', marginTop: 4, marginLeft: 28 }}>
            Make this template available to all users
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onCreate}
            style={{
              flex: 1,
              background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
              border: 'none',
              color: '#0F172A',
              padding: '14px',
              borderRadius: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Create Template
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '14px 24px',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: '#a1a1aa',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPage;
